/**
 * buildDealScenario — compact, QM-ready loan-scenario snapshot of a deal.
 *
 * Input is a camelCase deal row (transformDealToCamelCase), optionally
 * carrying ariveLoanData (the parsed Arive webhook payload) when the deal is
 * Arive-linked. Output feeds pageContext.referenceData.dealScenario on deal/
 * workspace pages so FutureBot can answer "what fits this deal?" without
 * re-asking for facts the screen already shows.
 *
 * Rules:
 *  - Only include facts we actually have — an omitted field simply doesn't
 *    filter in QM, but a guessed one silently eliminates valid programs.
 *  - Raw Origin/Arive values pass through untouched (PrimaryResidence,
 *    SingleFamily, NonQM, "Cash-Out Refinance", ...) — QM normalizes them.
 *  - Unmappable placeholders ("Not sure yet", "Other", "Affordable") are
 *    dropped per the QM field-mapping reference (docs/quantum-matrix-api.md §9).
 */

const UNMAPPABLE = /not\s*sure|^other$|^unknown$|^affordable$/i;

function isMeaningful(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string') return value.trim() !== '';
  return true;
}

/** Set scenario[key] = value only when the value is a real fact. */
function put(scenario, key, value) {
  if (!isMeaningful(value)) return;
  if (typeof value === 'string' && UNMAPPABLE.test(value.trim())) return;
  scenario[key] = typeof value === 'string' ? value.trim() : value;
}

/**
 * @param {object|null} deal camelCase deal (transformDealToCamelCase output)
 * @param {object} [opts]
 * @param {string} [opts.borrowerName] display name of the borrower/contact
 * @returns {object|null} scenario block, or null when there's nothing useful
 */
export function buildDealScenario(deal, { borrowerName } = {}) {
  if (!deal || typeof deal !== 'object') return null;
  const arive = deal.ariveLoanData && typeof deal.ariveLoanData === 'object' ? deal.ariveLoanData : {};
  const scenario = {};

  put(scenario, 'dealId', deal.id);
  put(scenario, 'dealName', deal.dealName);
  put(scenario, 'borrower', borrowerName || (arive.borrowerFirstName && `${arive.borrowerFirstName} ${arive.borrowerLastName || ''}`.trim()));
  put(scenario, 'stage', deal.stage);

  // Credit + amounts: Arive figures win (they're synced from the LOS).
  put(scenario, 'fico', arive.fico ?? arive.estimatedFICO ?? deal.creditScore);
  put(scenario, 'loanAmount', deal.loanAmount ?? arive.baseLoanAmount ?? arive.totalLoanAmount ?? deal.amount);
  put(scenario, 'downPayment', deal.downPayment ?? arive.downPayment);
  put(scenario, 'purchasePrice', arive.purchasePrice);
  put(scenario, 'ltv', arive.ltv);
  put(scenario, 'dtiFront', arive.frontEndDTI);
  put(scenario, 'dtiBack', arive.backEndDTI);

  // Property + occupancy (raw values are QM-accepted).
  put(scenario, 'state', deal.propertyAddress?.state || arive.propertyState);
  put(scenario, 'propertyType', deal.propertyType || arive.propertyType);
  put(scenario, 'occupancy', deal.occupancyType || arive.propertyUsage);

  // Transaction: loan_purpose is the more specific value ("Cash-Out
  // Refinance"), purchase_or_refi the coarse one.
  put(scenario, 'transactionType', deal.loanPurpose || deal.purchaseOrRefi);

  // Program hints: Arive mortgageType maps directly to a QM category
  // (NonQM -> Non-QM, USDARuralDevelopment -> USDA, ...); fall back to the
  // Origin product field.
  put(scenario, 'programCategory', arive.mortgageType || deal.product);
  put(scenario, 'loanTerm', deal.loanTerm);
  put(scenario, 'lender', deal.lender || arive.lenderName);
  put(scenario, 'interestRate', deal.interestRate ?? arive.noteRate);

  // A scenario that is only identity (id/name/borrower/stage) carries no loan
  // facts — not useful for QM.
  const factKeys = Object.keys(scenario).filter(
    (k) => !['dealId', 'dealName', 'borrower', 'stage'].includes(k)
  );
  return factKeys.length > 0 ? scenario : null;
}

/** One-line summary of the scenario for stateSummary / logging. */
export function describeDealScenario(scenario) {
  if (!scenario) return null;
  const bits = [];
  if (scenario.fico) bits.push(`${scenario.fico} FICO`);
  if (scenario.loanAmount) bits.push(`$${Number(scenario.loanAmount).toLocaleString()} loan`);
  if (scenario.ltv) bits.push(`${scenario.ltv}% LTV`);
  if (scenario.state) bits.push(scenario.state);
  if (scenario.occupancy) bits.push(String(scenario.occupancy));
  if (scenario.propertyType) bits.push(String(scenario.propertyType));
  if (scenario.transactionType) bits.push(String(scenario.transactionType));
  return bits.join(' · ') || null;
}

export default buildDealScenario;
